// @ts-ignore
import { Consumer, Subscription } from "@rails/actioncable";
import { User } from "../../core/types";

enum ChannelAction {
  ACCOUNT_USER_CREATED = "account_user_created",
  ACCOUNT_USER_UPDATED = "account_user_updated",
  ACCOUNT_USER_DESTROYED = "account_user_destroyed"
}

/**
 * Channel response from the Rails Chat::AccountChannel. Modeled as a discriminated
 * union on `action` so callers narrow `body` automatically. Every action we currently
 * handle carries a `User` row in `body`; an action-less response can still arrive
 * (server status pings) and leaves `body` typed as `unknown`.
 */
type ChannelResponse = { ok: boolean; status: number } & (
  | { action: ChannelAction; body: User }
  | { action?: undefined; body?: unknown }
);

export interface AccountCallback {
  onConnected?: () => void;
  onDisconnected?: () => void;
  onRejected?: () => void;
  onAccountUserCreated?: (data: { accountUser: User }) => void;
  onAccountUserUpdated?: (data: { accountUser: User }) => void;
  onAccountUserDestroyed?: (data: { accountUser: User }) => void;
  onError?: (data: { status: number }) => void;
}

export class AccountChannel {
  protected readonly CHANNEL_NAME = "Chat::AccountChannel";
  private subscription: Subscription | null = null;
  private consumer: Consumer;

  constructor(consumer: Consumer) {
    this.consumer = consumer;
  }

  onConnected(callback: AccountCallback): void {
    console.log(`Connected to ${this.CHANNEL_NAME}`);
    callback.onConnected?.();
  }

  onDisconnected(callback: AccountCallback): void {
    console.log(`Disconnected from ${this.CHANNEL_NAME}`);
    callback.onDisconnected?.();
  }

  onRejected(callback: AccountCallback): void {
    console.log(`Subscription to ${this.CHANNEL_NAME} was rejected`);
    callback.onRejected?.();
  }

  onReceived(response: ChannelResponse, callback: AccountCallback): void {
    if (!response.ok) {
      console.error(`${this.CHANNEL_NAME} error:`, response);
      callback.onError?.({ status: response.status });
      return;
    }

    switch (response.action) {
      case ChannelAction.ACCOUNT_USER_CREATED:
        callback.onAccountUserCreated?.({ accountUser: response.body });
        return;
      case ChannelAction.ACCOUNT_USER_UPDATED:
        callback.onAccountUserUpdated?.({ accountUser: response.body });
        return;
      case ChannelAction.ACCOUNT_USER_DESTROYED:
        callback.onAccountUserDestroyed?.({ accountUser: response.body });
        return;
    }
  }

  subscribe(callback: AccountCallback = {}): Subscription {
    // Ensure that we unsubscribe from any existing subscription before creating a new one
    this.unsubscribe();

    // Create a new subscription
    this.subscription = this.consumer.subscriptions.create({ channel: this.CHANNEL_NAME }, {
      connected: () => this.onConnected(callback),
      disconnected: () => this.onDisconnected(callback),
      rejected: () => this.onRejected(callback),
      received: (response: ChannelResponse) => this.onReceived(response, callback)
    });
    return this.subscription as Subscription;
  }

  unsubscribe(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
  }
}
